/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import useSessionStore from 'stores/useSessionStore';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import AnalysisOpinionSection, { OpinionSaveHandle } from '../../components/AnalysisOpinionSection';
import SectionBox from '../../components/SectionBox';
import { canExecuteDml, errorMessage, fetchElecRmk, updateElecOverallRmk } from '../api';
import { useUtComparisonElecStore } from '../store/useUtComparisonElecStore';
import { ELEC_AI_OPIN_TP_CDS } from '../utils/compareElec';

/**
 * "UT Matrix 정합화 Report"에서 불러온 이력(historyRptId)일 때만 표시되는 전체 종합의견 편집
 * 패널. 기계 화면의 HistoryOpinionPanel과 자리는 같지만, 전기는 UT 항목별 구분이 없어(전압유형이
 * 데이터마다 동적으로 달라짐) 항목별 표는 없이 RPT_M.RMK(전체 종합의견) 하나만 편집한다.
 */
const ElecOpinionPanel = () => {
  const historyRptId = useUtComparisonElecStore((s) => s.historyRptId);
  const userSession = useSessionStore();

  const [rmk, setRmk] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  /**
   * 하단 저장 버튼 하나로 모으기 위해 각 섹션의 save()를 직접 호출한다.
   * 섹션이 둘(규칙 기반 ④ / AI ⑤)이라 ref도 둘이다.
   */
  const ruleRef = useRef<OpinionSaveHandle>(null);
  const aiRef = useRef<OpinionSaveHandle>(null);

  useEffect(() => {
    if (!historyRptId) return;
    let alive = true;
    setLoading(true);
    setError('');
    fetchElecRmk(historyRptId)
      .then((v) => alive && setRmk(v))
      .catch((e) => alive && setError(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [historyRptId]);

  if (!historyRptId) return null;
  const canSave = canExecuteDml();

  /**
   * 탭 하단 저장 — 항목별 분석 결과(OPIN_M)와 전체 종합의견(RPT_M.RMK)을 이 버튼 하나로 저장한다.
   *
   * 저장처가 서로 달라(OPIN_M / RPT_M.RMK) 요청이 여러 번 나가지만, 사용자에게는 한 번의
   * 저장으로 보인다. 기계 탭(OpinionSection.saveAll)과 같은 구조다.
   *
   * ⚠ 두 섹션은 **순서대로** 저장해야 한다. saveRptOpinions가 의견 행을 통째로 덮어쓰므로,
   *   각 섹션이 저장 직전에 최신 상태를 다시 읽어 상대 섹션의 행을 보존한다. 동시에 보내면
   *   나중 요청이 앞 요청의 결과를 못 읽어 덮어쓴다.
   */
  const saveAll = async () => {
    if (!userSession.userId) return;
    setSaving(true);
    try {
      await ruleRef.current?.save();
      await aiRef.current?.save();
      await updateElecOverallRmk(historyRptId, rmk, userSession.userId);
      setSavedMsg('저장됨');
      window.setTimeout(() => setSavedMsg(''), 2000);
    } catch (e) {
      window.alert('종합의견 저장 오류: ' + errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/*
        항목 목록은 두 섹션 모두 공통코드 ANLS_OPIN_TP_CD(OPT_VAL_CTN1='ELEC')에서 가져오고,
        **생성 주체로 갈라 두 SectionBox로 나눈다** — 기계 탭이 ⑥(규칙 기반 UT 9종)과
        ⑦(AI 2항목)을 나눠 놓은 것과 같은 구성이다.

          ④ 전기사양 분석 : A-0001~0003 — 규칙 기반 → ANLS_RLT_CTN
          ⑤ 종합 분석 / 종합의견 : A-0004 — AI 생성 → AI_ANS_CTN

        컬럼이 갈리는 이유는 재생성 때 어느 쪽이 덮어써야 하는지 판단하기 위해서다.
        → pages/ut/ref/BE-REQUEST-elec-ai-power-analysis.md

        ⚠ ④는 항목을 나열(onlyCodes)하지 않고 **AI 코드만 제외(excludeCodes)** 한다.
          항목 목록의 출처는 공통코드 하나여야 하므로, 나중에 규칙 기반 항목이 추가되면
          ④에 자동으로 나타나야 한다.

        기계와 달리 항목별 "검토 의견" 칸은 두지 않는다(showOpinion=false) — 아래에 전체
        종합의견(RPT_M.RMK) 입력칸이 따로 있기 때문이다. 대신 생성된 문구를 사용자가 직접
        다듬을 수 있게 열어둔다(editableAnalysis).
      */}
      <SectionBox no="④" title="전기사양 분석" note={historyRptId}>
        {loading && <p className="hint-text">불러오는 중…</p>}
        {error && <div className="error-box">{error}</div>}
        {!loading && !error && (
          <AnalysisOpinionSection
            ref={ruleRef}
            rptId={historyRptId}
            atcDivsCd="ELEC"
            analysisLabel="분석 결과"
            editableAnalysis
            analysisField="anlsRltCtn"
            excludeCodes={ELEC_AI_OPIN_TP_CDS}
            /* 이 섹션엔 AI 항목이 없어 진행 표시가 필요 없다 */
            useAiProgress={false}
            showOpinion={false}
            hideSaveButton
          />
        )}
      </SectionBox>

      {/* 불러오는 중·오류 문구는 ④가 띄우므로 이 상자는 그때 아예 그리지 않는다 */}
      {!loading && !error && (
        <SectionBox no="⑤" title="종합 분석 / 종합의견">
          <>
            {/*
              AI 진행상태 표시를 켠다(useAiProgress 기본값). 진행 여부는 aiFieldCodes 항목만
              보고 판단하므로, ④의 규칙 기반 항목이 이미 채워져 있어도 A-0004가 비어 있으면
              "AI 분석 중"으로 잡히고 도착하면 폴링이 주워온다.

              제목은 기계 탭 ⑦의 '종합분석(AI)'과 같은 문구로 맞춘다. 저장되는 항목코드는
              그대로다 — 공통코드명('전력 사용량 종합분석')을 이 문구로 바꾸면 이 덮어쓰기는
              지우면 된다.
            */}
            <AnalysisOpinionSection
              ref={aiRef}
              rptId={historyRptId}
              atcDivsCd="ELEC"
              editableAnalysis
              aiFieldCodes={ELEC_AI_OPIN_TP_CDS}
              onlyCodes={ELEC_AI_OPIN_TP_CDS}
              itemTitles={{ 'A-0004': '종합분석(AI)' }}
              showOpinion={false}
              hideSaveButton
            />
            <p className="ai-block-title">종합의견</p>
            <textarea
              className="opin-input"
              value={rmk}
              onChange={(e) => setRmk(e.target.value)}
              disabled={!canSave}
              placeholder={
                canSave ? '전체 종합의견을 입력하세요' : 'ELMAPP 로그인 시에만 입력할 수 있습니다.'
              }
            />
          </>
        </SectionBox>
      )}
      {/*
        저장 버튼은 ④ 상자 밖에 둔다 — 이 버튼 하나가 항목별 분석결과와 종합의견을 함께
        저장하므로, 섹션을 접어도 계속 보여야 한다 (기계 탭 ⑦과 같은 구성).
      */}
      {!loading && !error && canSave && (
        <GlobalBtnGroup>
          {savedMsg && <span className="saved-msg">{savedMsg}</span>}
          <Button css={IconButton.button} className="save" onClick={saveAll} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default ElecOpinionPanel;
